import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import PaymentIcon from '@material-ui/icons/Payment';
import WarningIcon from '@material-ui/icons/Warning';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  List,
  ListItem,
  Select,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Paper,
  Container,
  Grid,
} from "@material-ui/core";
import { format } from "date-fns";
import { useAuth } from "../../context/authContext";
import {
  confirmPasswordError,
  emailError,
  editCurrentUser,
  getActiveSubscription,
  getCurrentUser,
  getOldPlan,
  newPasswordError,
  validEmail,
  validPassword,
} from "../../context/authContext/actions";
import CustomSnackbar from "../../components/snackbar";
import { currencies } from "../../constants";
import { currencyChanged } from "../../context/cellarsContext/actions";
import { useCellarsContext } from "../../context/cellarsContext";
import useStyles from "../../components/util/styles";
import { LayoutWrapper } from "../../components/layoutWrapper";
import delectableLogo from "../../images/delectable_logo.png";
import ConfirmCancelSubscriptionDialog from "../../components/confirmCancelSubscriptionDialog";
import { cancelSubscriptionRequest, getCustomerPortalLinkRequest } from "../../lib/subscriptionRequests";

const roleOptions = [
  "Enthusiast",
  "Retailer",
  "Restaurant",
  "Winemaker",
  "Distributor",
  "Importer",
  "Broker",
  "Auction House",
  "Media",
];

export type SettingsFormState = {
  email?: string;
  emailError: string;
  password: string;
  newPassword: string;
  newPasswordError: string;
  confirmNewPassword: string;
  confirmNewPasswordError: string;
  firstName: string;
  lastName?: string;
  role: string;
  street_address?: string;
  street_address_line2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  currency: string;
};

export type ActivePlan = {
  symbol: string;
  plan: string;
  price: string;
  renewsAt?: number;
  canceledAt?: number;
  status: string;
};

const Settings: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const {
    state: {
      loading,
      user,
      oldPlan,
      isPremiumUser,
      isProUser,
      paymentCurrency,
      //paymentMethods,
      renewsAt,
      canceledAt,
      isOnFreeTrialUntil,
      planPrice,
      planInterval,
      subscription
    },
    dispatch: authDispatch,
  } = useAuth();
  const { dispatch: cellarDispatch } = useCellarsContext();
  const [currencyChange, setCurrencyChange] = React.useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [activePlan, setActivePlan] = useState<ActivePlan>({
    symbol: "$",
    plan: "Basic",
    price: "0",
    renewsAt: undefined,
    canceledAt: undefined,
    status: '',
  });
  const [settingsForm, updateSettingsForm] = React.useState<SettingsFormState>({
    email: "",
    emailError: "",
    password: "",
    newPassword: "",
    newPasswordError: "",
    confirmNewPassword: "",
    confirmNewPasswordError: "",
    firstName: "",
    lastName: "",
    role: "",
    street_address: "",
    street_address_line2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    currency: "USD",
  });

  const disableSaveButton =
    settingsForm.emailError.length > 0 ||
    settingsForm.newPasswordError.length > 0 ||
    settingsForm.confirmNewPasswordError.length > 0;
    

  React.useEffect(() => {
    if (!loading && !user) {
      getCurrentUser(authDispatch);
    } else if (user) {
      updateSettingsForm((prevState) => {
        return {
          ...prevState,
          email: user.email ? user.email : "",
          emailError: "",
          firstName: user.name ? user.name : "",
          lastName: user.last_name ? user.last_name : "",
          role: user.role ? user.role : "",
          street_address: user.street_address ? user.street_address : "",
          street_address_line2: user.street_address_line2
            ? user.street_address_line2
            : "",
          city: user.city ? user.city : "",
          state: user.state ? user.state : "",
          zip: user.zip ? user.zip : "",
          country: user.country ? user.country : "",
          currency: user.currency,
        };
      });
    }
  }, [loading, user, authDispatch]);


  React.useEffect(() => {
    const now = Date.now();
    if (!oldPlan && user && user.old_sub_id) {
      getOldPlan(authDispatch, user.old_sub_id);
    }
    if (isPremiumUser === undefined) {
      getActiveSubscription(authDispatch);
    }
    if (oldPlan) {
      const currency = currencies.find((c) => c.value === oldPlan.currency);
      setActivePlan((prevState) => {
        if (oldPlan && oldPlan.renewsAt && Date.parse(oldPlan.renewsAt) > now) {
          return {
            symbol: currency ? currency.symbol : prevState.symbol,
            plan: oldPlan.plan,
            price: oldPlan.price,
            renewsAt: Date.parse(oldPlan.renewsAt),
            canceledAt: undefined,
            status: "active",
          };
        }
        return prevState;
      });
    }
    if (
      !oldPlan ||
      !oldPlan.renewsAt ||
      Date.parse(oldPlan.renewsAt) <= now ||
      (Date.parse(oldPlan.renewsAt) > now && (isPremiumUser || isProUser))
    ) {

      const currency = currencies.find((c) => c.value === paymentCurrency);
      const plan = isProUser ? "Pro" : isPremiumUser ? "Premium" : "Basic";
      const price = planPrice ? (planPrice / 100 ).toString() : "";

      setActivePlan((prevState) => {
        return {
          symbol: currency ? currency.symbol : prevState.symbol,
          plan,
          price,
          renewsAt,
          canceledAt,
          status: subscription?.status || "canceled",
        };
      });
    }
  }, [
    loading,
    isPremiumUser,
    isProUser,
    oldPlan,
    authDispatch,
    paymentCurrency,
    user,
    renewsAt,
    canceledAt,
    isOnFreeTrialUntil,
    planPrice,
    planInterval,
  ]);

  const changingPassword = () => {
    return (
      settingsForm.newPassword !== "" ||
      settingsForm.confirmNewPassword !== "" ||
      settingsForm.password !== ""
    );
  };

  const getNewPasswordFieldError = (
    currentPassword: string,
    nextNewPassword: string
  ) => {
    if (!currentPassword && !nextNewPassword) return "";
    return newPasswordError(currentPassword, nextNewPassword);
  };

  const getConfirmNewPasswordFieldError = (
    currentPassword: string,
    nextNewPassword: string,
    nextConfirmNewPassword: string
  ) => {
    if (!currentPassword && !nextConfirmNewPassword) return "";
    if (!nextConfirmNewPassword) return "Please confirm your new password.";
    return confirmPasswordError(nextNewPassword, nextConfirmNewPassword);
  };

  const saveSettings = async (e: any) => {
    e.preventDefault();
    if (user) {
      const isChangingEmail = settingsForm.email !== (user.email || "");

      if (isChangingEmail && !validEmail(settingsForm.email || "")) {
        updateSettingsForm({
          ...settingsForm,
          emailError: emailError(settingsForm.email || ""),
        });
        return;
      }

      const userData = { id: user.id, ...settingsForm };
      if (changingPassword()) {
        // User is trying to change the password, validate
        validPassword(settingsForm.newPassword) &&
          validPassword(settingsForm.confirmNewPassword) &&
          settingsForm.password !== settingsForm.newPassword &&
          settingsForm.newPassword === settingsForm.confirmNewPassword &&
          (await editCurrentUser(userData, authDispatch));

        updateSettingsForm({
          ...settingsForm,
          newPasswordError: newPasswordError(
            settingsForm.password,
            settingsForm.newPassword
          ),
          confirmNewPasswordError: confirmPasswordError(
            settingsForm.newPassword,
            settingsForm.confirmNewPassword
          ),
        });
      } else {
        // Skip validation otherwise
        await editCurrentUser(userData, authDispatch);
        if (currencyChange) {
          await currencyChanged(cellarDispatch, true);
        }
      }
    }
  };

  const handleChange = (e: any) => {
    e.preventDefault();
    updateSettingsForm({
      ...settingsForm,
      role: e.target.value,
    });
  };

  const handleCancelSubscription = async () => {
    await cancelSubscriptionRequest();
    setConfirmDialogOpen(false);
    window.location.reload();
  };

  const handleStripeCustomerPortal = async (e: any) => {
    e.preventDefault();
    const res = await getCustomerPortalLinkRequest();
    if (res && res.url) {
      window.location.href = res.url;
    }
  };

  return (
    <LayoutWrapper pathName="settings">
      <Container maxWidth="lg" className={classes.container}>
        <CustomSnackbar />
        <ConfirmCancelSubscriptionDialog
          planDescription={`You are currently subscribed to plan: Cellar Watch ${activePlan.plan} (${activePlan.symbol}${activePlan.price}/${planInterval})`}
          open={confirmDialogOpen}
          setOpen={setConfirmDialogOpen}
          handleCancelSubscription={handleCancelSubscription}
        />
        <Paper className={classes.paper}>
          <form className={classes.form} noValidate autoComplete="off">
            <input
              type="text"
              name="username"
              autoComplete="username"
              style={{ display: "none" }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              style={{ display: "none" }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <List>
                  <ListItem>
                    <Typography component="h1" variant="h5">
                      Profile
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      autoFocus
                      variant="outlined"
                      id="email"
                      label="Email Address"
                      name="email"
                      value={settingsForm.email}
                      error={settingsForm.emailError.length > 0}
                      helperText={settingsForm.emailError}
                      onChange={(e) => 
                        updateSettingsForm({
                          ...settingsForm,
                          email: e.target.value,
                          emailError: emailError(e.target.value),
                        })
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      required
                      autoComplete="fname"
                      name="firstName"
                      id="firstName"
                      label="First Name"
                      value={settingsForm.firstName}
                      onChange={(e) =>
                        updateSettingsForm({
                          ...settingsForm,
                          firstName: e.target.value,
                        })
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      required
                      autoComplete="lname"
                      id="lastName"
                      label="Last Name"
                      name="lastName"
                      value={settingsForm.lastName}
                      onChange={(e) =>
                        updateSettingsForm({
                          ...settingsForm,
                          lastName: e.target.value,
                        })
                      }
                    />
                  </ListItem>
                  <ListItem style={{ marginBottom: 4 }}>
                    <FormControl
                      className={classes.settingsField}
                      required
                      variant={"filled"}
                      size="medium"
                    >
                      <InputLabel shrink htmlFor="age-native-label-placeholder">
                        I'm a
                      </InputLabel>
                      <Select
                        native
                        value={settingsForm.role}
                        onChange={(e) => handleChange(e)}
                      >
                        {roleOptions.map((option: string) => (
                          <option value={option} key={option}>
                            {option}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      required
                      autoComplete="streetAddress"
                      id="streetAddress"
                      label="Street Address"
                      name="streetAddress"
                      value={settingsForm.street_address}
                      onChange={(e) =>
                        updateSettingsForm({
                          ...settingsForm,
                          street_address: e.target.value,
                        })
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      autoComplete="addressContd"
                      id="addressContd"
                      label="Address (contd.)"
                      name="addressContd"
                      value={settingsForm.street_address_line2}
                      onChange={(e) =>
                        updateSettingsForm({
                          ...settingsForm,
                          street_address_line2: e.target.value,
                        })
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      required
                      autoComplete="city"
                      id="city"
                      label="City"
                      name="city"
                      value={settingsForm.city}
                      onChange={(e) =>
                        updateSettingsForm({
                          ...settingsForm,
                          city: e.target.value,
                        })
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      required
                      autoComplete="state"
                      id="state"
                      label="State/Province/Region"
                      name="state"
                      value={settingsForm.state}
                      onChange={(e) =>
                        updateSettingsForm({
                          ...settingsForm,
                          state: e.target.value,
                        })
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      required
                      autoComplete="zip"
                      id="zip"
                      label="Zip/Postal Code"
                      name="zip"
                      value={settingsForm.zip}
                      onChange={(e) =>
                        updateSettingsForm({
                          ...settingsForm,
                          zip: e.target.value,
                        })
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      required
                      autoComplete="country"
                      id="country"
                      label="Country"
                      name="country"
                      value={settingsForm.country}
                      onChange={(e) =>
                        updateSettingsForm({
                          ...settingsForm,
                          country: e.target.value,
                        })
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <List>
                  <ListItem>
                    <Typography component="h1" variant="h5">
                      Subscription
                    </Typography>
                  </ListItem>
                  {activePlan.status === "active" || activePlan.status === "trialing" || activePlan.status === "past_due" ? (
                    <>
                      <ListItem>
                        <Typography style={{ color: "#b7b7c7" }}>
                          {`You are currently subscribed to plan: Cellar Watch ${activePlan.plan} (${activePlan.symbol}${activePlan.price}/${planInterval})`}
                        </Typography>
                      </ListItem>
                      {activePlan.renewsAt && !isOnFreeTrialUntil && (
                        <ListItem>
                          <Typography style={{ color: "#b7b7c7", display: "flex", alignItems: "center" }}>
                            {activePlan.status === "past_due" ? (
                              <>
                                <WarningIcon style={{ color: "#FFA726", marginRight: 8 }} />
                                Your latest payment has failed. Update your payment method to continue this subscription.
                              </>
                            ) : activePlan.canceledAt ? (
                              `Your subscription will be canceled automatically on ${format(
                                activePlan.canceledAt,
                                "MMMM do, yyyy"
                              )}`
                            ) : (
                              `Your subscription will automatically renew on ${format(
                                activePlan.renewsAt,
                                "MMMM do, yyyy"
                              )}`
                            )}
                          </Typography>
                        </ListItem>
                      )}
                      {!!isOnFreeTrialUntil || activePlan.status === "trialing" && (
                        <ListItem>
                          <Typography style={{ color: "#b7b7c7" }}>
                            {`You are on a free trial until ${format(
                              isOnFreeTrialUntil || activePlan.renewsAt || 0,
                              "MMMM do, yyyy"
                            )}`}
                          </Typography>
                        </ListItem>
                      )}
                      <ListItem>
                        <Button
                          startIcon={<PaymentIcon />}
                          variant={"outlined"}
                          color="primary"
                          onClick={handleStripeCustomerPortal}
                          href="#"
                        >
                          {"Manage Subscription/Billing history"}
                        </Button>
                      </ListItem>
                    </>
                  ) : (
                    <>
                      <ListItem>
                        <Typography style={{ color: "#b7b7c7" }}>
                          You don't have an active subscription.
                        </Typography>
                      </ListItem>
                      <ListItem>
                          <Button
                            variant={"outlined"}
                            color="primary"
                            onClick={(e) => {
                              e.preventDefault();
                              history.push("/select-plan");
                            }}
                            href="#"
                          >
                            {"View Subscription Options"}
                          </Button>
                        </ListItem>
                      </>
                  )}
                  <ListItem>
                    <Typography component="h1" variant="h5">
                      Password
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      required
                      autoComplete="new-password"
                      name="settings-current-password"
                      label="Current Password"
                      type="password"
                      id="current-password"
                      value={settingsForm.password}
                      onChange={(e) => {
                        const nextCurrentPassword = e.target.value;
                        const nextNewPassword = settingsForm.newPassword;
                        const nextConfirmNewPassword =
                          settingsForm.confirmNewPassword;

                        updateSettingsForm({
                          ...settingsForm,
                          password: nextCurrentPassword,
                          newPasswordError: getNewPasswordFieldError(
                            nextCurrentPassword,
                            nextNewPassword
                          ),
                          confirmNewPasswordError:
                            getConfirmNewPasswordFieldError(
                              nextCurrentPassword,
                              nextNewPassword,
                              nextConfirmNewPassword
                            ),
                        });
                      }}
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      required
                      autoComplete="new-password"
                      name="settings-new-password"
                      label="New Password"
                      type="password"
                      id="new-password"
                      value={settingsForm.newPassword}
                      error={settingsForm.newPasswordError.length > 0}
                      helperText={settingsForm.newPasswordError}
                      onChange={(e) => {
                        const nextNewPassword = e.target.value;
                        const nextCurrentPassword = settingsForm.password;
                        const nextConfirmNewPassword =
                          settingsForm.confirmNewPassword;

                        updateSettingsForm({
                          ...settingsForm,
                          newPassword: nextNewPassword,
                          newPasswordError: getNewPasswordFieldError(
                            nextCurrentPassword,
                            nextNewPassword
                          ),
                          confirmNewPasswordError:
                            getConfirmNewPasswordFieldError(
                              nextCurrentPassword,
                              nextNewPassword,
                              nextConfirmNewPassword
                            ),
                        });
                      }}
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      className={classes.settingsField}
                      variant="outlined"
                      required
                      autoComplete="new-password"
                      name="settings-new-password-confirmation"
                      label="Confirm New Password"
                      type="password"
                      id="new-password-confirmation"
                      value={settingsForm.confirmNewPassword}
                      error={settingsForm.confirmNewPasswordError.length > 0}
                      helperText={settingsForm.confirmNewPasswordError}
                      onChange={(e) => {
                        const nextConfirmNewPassword = e.target.value;

                        updateSettingsForm({
                          ...settingsForm,
                          confirmNewPassword: nextConfirmNewPassword,
                          confirmNewPasswordError:
                            getConfirmNewPasswordFieldError(
                              settingsForm.password,
                              settingsForm.newPassword,
                              nextConfirmNewPassword
                            ),
                          newPasswordError: getNewPasswordFieldError(
                            settingsForm.password,
                            settingsForm.newPassword,
                          ),
                        });
                      }}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <List>
                  <ListItem>
                    <Typography component="h1" variant="h5">
                      Currency
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <RadioGroup
                      aria-label="currency"
                      name="currency"
                      value={settingsForm.currency}
                      onChange={(e) => {
                        setCurrencyChange(true);
                        updateSettingsForm({
                          ...settingsForm,
                          currency: e.target.value,
                        });
                      }}
                    >
                      {currencies.map(({ value, label }) => (
                        <FormControlLabel
                          value={value}
                          control={<Radio color={"primary"} />}
                          label={label}
                          key={label}
                        />
                      ))}
                    </RadioGroup>
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            <Box mt={5} style={{ display: "flex", justifyContent: "center" }}>
              <Button
                startIcon={
                  <img
                    style={{ width: 32, height: 32 }}
                    src={delectableLogo}
                    alt="Delectable Logo"
                  />
                }
                type="submit"
                variant="outlined"
                className={classes.settingsField}
                href="#"
              >
                My Delectable Account
              </Button>
            </Box>
            <Box style={{ display: "flex", justifyContent: "center" }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submit}
                onClick={(e) => saveSettings(e)}
                href="#"
                disabled={disableSaveButton || loading}
              >
                Save
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </LayoutWrapper>
  );
};

export default Settings;
